import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import AppContext from '../../contexts/AppContext'
import { ensureStatsReadyForRoleplay } from '../../utils/statsReallocGate'
import './Navbar.css'

const NAV_LINKS = [
  { to: '/', label: 'Inicio', icon: '⛩', end: true },
  { to: '/hans', label: 'Territorios', icon: '◈' },
  { to: '/aventuras', label: 'Aventuras', icon: '⚔' },
]

const NAV_LINKS_AUTH = [
  { to: '/personajes', label: 'Shinobis', icon: '忍' },
  { to: '/clanes', label: 'Clanes', icon: '◎' },
  { to: '/sistemas-de-rol', label: 'Sistemas', icon: '制' },
]

export default function Navbar() {
  const { logout, user, character, setCharacter, hasPermission } = useContext(AppContext)
  const [isNavOpen, setIsNavOpen] = useState(false)
  const [isUserOpen, setIsUserOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const dropdownRef = useRef(null)
  const navRef = useRef(null)

  const pathname = location.pathname
  const territoryMatch = pathname.match(/^\/han\/([^/]+)/)
  const territorySlug = territoryMatch ? territoryMatch[1] : null
  const showCTA = !!(user && territorySlug)

  const toggleMobile = () => setIsNavOpen((v) => !v)
  const toggleUser = () => setIsUserOpen((v) => !v)
  const closeAll = () => {
    setIsNavOpen(false)
    setIsUserOpen(false)
  }

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') closeAll() }
    const onMouseDown = (e) => {
      const target = e.target
      const clickedInDropdown = dropdownRef.current && dropdownRef.current.contains(target)
      const clickedInNav = navRef.current && navRef.current.contains(target)
      const isToggleButton =
        (target.closest && target.closest('.navbar__mobile-toggle')) ||
        (target.closest && target.closest('.navbar__user-button'))
      if (!clickedInDropdown && !isToggleButton) setIsUserOpen(false)
      if (!clickedInNav && !isToggleButton) setIsNavOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [])

  useEffect(() => {
    closeAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleNavigate = () => closeAll()

  const allLinks = user
    ? [...NAV_LINKS, ...NAV_LINKS_AUTH]
    : NAV_LINKS

  return (
    <nav className="navbar" role="navigation" aria-label="Main">
      {/* Franja superior decorativa */}
      <div className="navbar__top-line" aria-hidden="true" />

      <div className="navbar__container">
        {/* ── Marca ──────────────────────────────────── */}
        <div className="navbar__brand">
          <Link to="/" className="navbar__brand-link" onClick={handleNavigate}>
            <div className="navbar__brand-emblem">
              <img src="/assets/images/shuriken.png" alt="" className="navbar__logo" />
            </div>
            <div className="navbar__brand-text-wrap">
              <span className="navbar__brand-text">Shinobis' War</span>
              <span className="navbar__brand-sub">Foro de rol</span>
            </div>
          </Link>
        </div>

        {/* ── Separador vertical izquierdo ───────────── */}
        <div className="navbar__sep" aria-hidden="true" />

        {/* ── Navegación principal ───────────────────── */}
        <div
          id="navbar-menu"
          ref={navRef}
          className={`navbar__nav ${isNavOpen ? 'is-open' : ''}`}
        >
          <ul className="navbar__list">
            {allLinks.map(({ to, label, icon, end }) => (
              <li key={to} className="navbar__item">
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => `navbar__link${isActive ? ' is-active' : ''}`}
                  onClick={handleNavigate}
                >
                  <span className="navbar__link-icon" aria-hidden="true">{icon}</span>
                  <span className="navbar__link-label">{label}</span>
                </NavLink>
              </li>
            ))}

            {showCTA && (
              <li className="navbar__item navbar__item--cta">
                <Link
                  to={`/aventura/crear/${territorySlug}`}
                  className="navbar__cta"
                  onClick={async (e) => {
                    e.preventDefault()
                    handleNavigate()
                    if (
                      !(await ensureStatsReadyForRoleplay({
                        character,
                        navigate,
                        setCharacter,
                      }))
                    ) {
                      return
                    }
                    navigate(`/aventura/crear/${territorySlug}`)
                  }}
                >
                  <span aria-hidden="true">✦</span>
                  <span>Crear aventura</span>
                </Link>
              </li>
            )}
          </ul>
        </div>

        {/* ── Separador vertical derecho ─────────────── */}
        <div className="navbar__sep" aria-hidden="true" />

        {/* ── Usuario / Auth ─────────────────────────── */}
        <div className="navbar__user" ref={dropdownRef}>
          {user && user.user_public_name ? (
            <div className="navbar__user-menu">
              <button
                className="navbar__user-button"
                onClick={toggleUser}
                aria-haspopup="menu"
                aria-expanded={isUserOpen}
                aria-controls="navbar-user-menu"
              >
                <div className="navbar__user-avatar" aria-hidden="true">
                  {user.user_public_name.charAt(0).toUpperCase()}
                </div>
                <span className="navbar__user-name">{user.user_public_name}</span>
                <span className={`navbar__dropdown-arrow ${isUserOpen ? 'is-open' : ''}`} aria-hidden="true">▾</span>
              </button>

              <div
                id="navbar-user-menu"
                className={`navbar__dropdown ${isUserOpen ? 'is-open' : ''}`}
                role="menu"
              >
                <div className="navbar__dropdown-header" aria-hidden="true">
                  <span>◈</span>
                  <span>{user.user_public_name}</span>
                </div>
                <Link to="/perfil" className="navbar__dropdown-item" onClick={handleNavigate} role="menuitem">
                  <span className="navbar__dropdown-icon" aria-hidden="true">◇</span>
                  <span>Perfil</span>
                </Link>
                <Link to="/mis-personajes" className="navbar__dropdown-item" onClick={handleNavigate} role="menuitem">
                  <span className="navbar__dropdown-icon" aria-hidden="true">忍</span>
                  <span>Mis shinobis</span>
                </Link>
                {hasPermission && hasPermission('admin.dashboard') && (
                  <Link to="/admin" className="navbar__dropdown-item navbar__dropdown-item--admin" onClick={handleNavigate} role="menuitem">
                    <span className="navbar__dropdown-icon" aria-hidden="true">◆</span>
                    <span>Administración</span>
                  </Link>
                )}
                <hr className="navbar__dropdown-divider" />
                <button
                  onClick={() => { logout(); closeAll() }}
                  className="navbar__dropdown-item navbar__dropdown-item--logout"
                  role="menuitem"
                >
                  <span className="navbar__dropdown-icon" aria-hidden="true">✦</span>
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="navbar__auth">
              <Link to="/entrar" className="navbar__auth-link navbar__auth-link--login" onClick={handleNavigate}>
                Entrar
              </Link>
              <Link to="/registro" className="navbar__auth-link navbar__auth-link--register" onClick={handleNavigate}>
                Registrarse
              </Link>
            </div>
          )}
        </div>

        {/* ── Toggle móvil ───────────────────────────── */}
        <button
          className="navbar__mobile-toggle"
          onClick={toggleMobile}
          aria-controls="navbar-menu"
          aria-expanded={isNavOpen}
          aria-label={isNavOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          <span className={`navbar__hamburger ${isNavOpen ? 'is-open' : ''}`} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
      </div>

      {/* Franja inferior decorativa */}
      <div className="navbar__bottom-line" aria-hidden="true" />
    </nav>
  )
}
